/**
 * Update / skill-update nudges — the FOREGROUND half of the notifier (the background, network half
 * is version-refresh.ts).
 *
 * The load-bearing guarantee: this path NEVER does synchronous/awaited network I/O. It shows the
 * nudge from the on-disk cache only (instant), and when a cache is stale it hands the npm/GitHub
 * lookup to a DETACHED child process that writes the cache for the NEXT run. So the current command
 * is never slowed by the network.
 */

import { findGitRoot } from './config-as-code/workspace.js';
import { isNewerVersion } from './version.js';
import {
  readCache,
  isUpdateCheckDisabled,
  isCacheStale,
  CLI_CACHE_FILE,
  SKILL_CACHE_FILE,
} from './version-cache.js';
import { findInstalledSkills, lowestInstalledVersion, SKILL_INSTALL_CMD } from './skill-check.js';
import { scheduleVersionRefresh } from './version-refresh.js';

/**
 * Show update nudges from the on-disk cache only (instant — never blocks), then schedule a detached
 * background refresh of any stale cache for the next run. Nudges go to stderr and are suppressed
 * when stderr isn't a TTY (pipes, redirects, logs) so machine-consumed output stays clean, or when
 * disabled by env/CI. Wrapped so it can never throw or delay CLI exit.
 *
 * @param current the running CLI version (from package.json)
 * @param entryPath the CLI entry file path to re-invoke for the background refresh
 */
export function showUpdateNudges(current: string, entryPath: string): void {
  if (isUpdateCheckDisabled() || !process.stderr.isTTY) return;
  try {
    // CLI: nudge from the last-known cached version of ANY age — keep nudging until they update.
    const cliLatest = readCache(CLI_CACHE_FILE)?.latest ?? null;
    if (cliLatest && isNewerVersion(cliLatest, current)) {
      console.error(`\nUpdate available: ${current} → ${cliLatest}\nRun \`rekor update\` to update.`);
    }

    // Skill: only relevant when a rekor skill is installed in this repo.
    const installs = findInstalledSkills(findGitRoot() ?? process.cwd());
    const localSkill = installs.length > 0 ? lowestInstalledVersion(installs) : null;
    if (localSkill) {
      const skillLatest = readCache(SKILL_CACHE_FILE)?.latest ?? null;
      if (skillLatest && isNewerVersion(skillLatest, localSkill)) {
        console.error(
          `\nSkill update available: ${localSkill} → ${skillLatest}\nRun \`${SKILL_INSTALL_CMD}\` to update.`,
        );
      }
    }

    // Refresh any stale cache in the background for the next run. The skill cache is only worth
    // refreshing when a skill is actually installed.
    const cliStale = isCacheStale(CLI_CACHE_FILE);
    const skillStale = localSkill !== null && isCacheStale(SKILL_CACHE_FILE);
    if (cliStale || skillStale) {
      scheduleVersionRefresh({ cli: cliStale, skill: skillStale }, entryPath);
    }
  } catch {
    // Never block the CLI on an update check.
  }
}
